import { normalizeMCPToolset } from '@/hooks/mcp/utils'

import type { MCPToolset } from '@/types/MCPServer'

export type MicrosoftSharePointAccessMode = 'tenant_wide' | 'selected_sites'
export type MicrosoftSelectedSitesGrantRole = 'write'
export const MICROSOFT_SELECTED_SITES_GRANT_ROLE: MicrosoftSelectedSitesGrantRole =
  'write'

export const MICROSOFT_SHAREPOINT_ACCESS_MODE_KEY = 'sharepoint_access_mode'
export const MICROSOFT_SELECTED_SITES_KEY = 'sharepoint_selected_sites'

export const MICROSOFT_CORE_PERMISSION_LABELS = [
  'offline_access',
  'User.Read',
] as const

const MICROSOFT_SHAREPOINT_TENANT_WIDE_PERMISSIONS = [
  'Files.Read.All',
  'Sites.Read.All',
] as const

const MICROSOFT_SHAREPOINT_SELECTED_SITE_PERMISSIONS = [
  'Sites.Selected',
] as const

const SELECTED_SITES_PERMISSION_TOOLSETS = new Set([
  'sharepoint',
  'word',
  'excel',
  'powerpoint',
])

const MICROSOFT_TOOLSET_PERMISSION_FALLBACKS: Record<string, string[]> = {
  onedrive: ['Files.Read.All', 'Files.ReadWrite.All'],
  'outlook calendar': [
    'Calendars.Read',
    'Calendars.ReadWrite',
    'Calendars.Read.Shared',
    'Calendars.ReadWrite.Shared',
  ],
  'outlook email': ['Mail.Read', 'Mail.ReadWrite', 'Mail.Send'],
  people: [
    'People.Read',
    'Contacts.Read',
    'Contacts.Read.Shared',
    'User.ReadBasic.All',
  ],
  powerpoint: ['Files.Read.All', 'Files.ReadWrite.All'],
  sharepoint: [...MICROSOFT_SHAREPOINT_TENANT_WIDE_PERMISSIONS],
  teams: [
    'Chat.Read',
    'Chat.ReadWrite',
    'Team.ReadBasic.All',
    'Channel.ReadBasic.All',
    'ChannelMessage.Read.All',
    'ChannelMessage.Send',
  ],
  word: ['Files.Read.All', 'Files.ReadWrite.All'],
  excel: ['Files.Read.All', 'Files.ReadWrite.All'],
}

const MICROSOFT_TOOLSET_NAME_ALIASES: Record<string, string> = {
  'one drive': 'onedrive',
  'outlook mail': 'outlook email',
  'power point': 'powerpoint',
  'share point': 'sharepoint',
}

const SELECTED_SITES_CONFLICTING_TOOLSETS = new Set(['onedrive'])

export const normalizeMicrosoftToolsetName = (toolsetName: string) => {
  const normalizedName = toolsetName
    .replace(/[_-]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
  return MICROSOFT_TOOLSET_NAME_ALIASES[normalizedName] ?? normalizedName
}

export const getMicrosoftSharePointAccessMode = (
  providerConfig: Record<string, any> | null | undefined,
): MicrosoftSharePointAccessMode =>
  providerConfig?.[MICROSOFT_SHAREPOINT_ACCESS_MODE_KEY] === 'selected_sites'
    ? 'selected_sites'
    : 'tenant_wide'

export const getMicrosoftSelectedSharePointSiteUrls = (
  providerConfig: Record<string, any> | null | undefined,
): string[] => {
  const rawValue = providerConfig?.[MICROSOFT_SELECTED_SITES_KEY]
  if (!Array.isArray(rawValue)) return []
  return rawValue.filter((value): value is string => typeof value === 'string')
}

export const getToolsetPermissionsByName = (
  toolsetName: string,
  sharepointAccessMode: MicrosoftSharePointAccessMode = 'tenant_wide',
): string[] => {
  const toolsetKey = normalizeMicrosoftToolsetName(toolsetName)

  const fallbackPermissions =
    sharepointAccessMode === 'selected_sites' &&
    SELECTED_SITES_PERMISSION_TOOLSETS.has(toolsetKey)
      ? [...MICROSOFT_SHAREPOINT_SELECTED_SITE_PERMISSIONS]
      : MICROSOFT_TOOLSET_PERMISSION_FALLBACKS[toolsetKey]

  if (!fallbackPermissions) return []
  return [...MICROSOFT_CORE_PERMISSION_LABELS, ...fallbackPermissions]
}

export const getToolsetPermissions = (
  toolset: Partial<MCPToolset> | null | undefined,
  sharepointAccessMode: MicrosoftSharePointAccessMode = 'tenant_wide',
): string[] => {
  const normalizedToolset = normalizeMCPToolset(toolset)
  if (!normalizedToolset) return []
  return getToolsetPermissionsByName(
    normalizedToolset.name,
    sharepointAccessMode,
  )
}

export const buildMicrosoftPermissionList = (
  toolsets: Array<Partial<MCPToolset> | null | undefined>,
  sharepointAccessMode: MicrosoftSharePointAccessMode = 'tenant_wide',
): string[] =>
  Array.from(
    new Set(
      toolsets.flatMap((toolset) =>
        getToolsetPermissions(toolset, sharepointAccessMode),
      ),
    ),
  )

export const hasSelectedSitesConflictingToolsets = (
  toolsets: Array<Partial<MCPToolset> | null | undefined>,
): boolean =>
  toolsets.some((toolset) => {
    const normalizedToolset = normalizeMCPToolset(toolset)
    if (!normalizedToolset) return false
    return SELECTED_SITES_CONFLICTING_TOOLSETS.has(
      normalizeMicrosoftToolsetName(normalizedToolset.name),
    )
  })

interface MicrosoftConsentPromptPolicyInput {
  usesCustomCredentials: boolean
  hasUserToken: boolean
  needsReauth: boolean
}

export const shouldForceMicrosoftConsentPrompt = ({
  usesCustomCredentials,
  hasUserToken,
  needsReauth,
}: MicrosoftConsentPromptPolicyInput): boolean => {
  // Microsoft recommends avoiding prompt=consent for ordinary sign-ins once
  // consent has already been granted. The integrations UI now exposes an
  // explicit org-consent path for managed Microsoft instead of inferring it
  // from user token state.
  void usesCustomCredentials
  void hasUserToken
  void needsReauth
  return false
}
